import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { soccerUrl } from '../utils/paths';
import { mediaUrl } from '../utils/blogUtils';
import './PublicMatchPreview.css';

const formatPosition = (position) => {
  if (!position) return '';
  if (position.length <= 4 && position === position.toUpperCase()) return position;
  return position.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const renderPlayer = (player) => (
  <div key={player.playerId} className="public-match-preview__player">
    {player.profilePictureUrl ? (
      <img
        src={mediaUrl(player.profilePictureUrl)}
        alt={player.playerName}
        className="public-match-preview__player-photo"
      />
    ) : (
      <div className="public-match-preview__player-photo public-match-preview__player-photo--placeholder">
        {(player.playerName || '?').charAt(0)}
      </div>
    )}
    <div className="public-match-preview__player-info">
      <p className="public-match-preview__player-name">
        {player.jerseyNumber != null ? `#${player.jerseyNumber} ` : ''}
        {player.playerName}
      </p>
      <p className="public-match-preview__player-meta">
        {[player.fieldPosition, formatPosition(player.defaultPosition)].filter(Boolean).join(' · ')}
      </p>
      {player.notes && <p className="public-match-preview__player-notes">{player.notes}</p>}
    </div>
  </div>
);

const PublicMatchPreview = () => {
  const { token } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPublicMatchPreview(token);
      setPage(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load match preview');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const shareUrl = soccerUrl(`/public/match-preview/${token}`);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError('Failed to copy link');
    }
  };

  const handleShareWhatsApp = () => {
    if (!page) return;
    const message =
      `⚽ *${page.title}*\n\n` +
      (page.opponentName ? `vs ${page.opponentName}\n` : '') +
      `📅 ${new Date(page.matchDate).toLocaleString()}\n` +
      (page.location ? `📍 ${page.location}\n` : '') +
      `\n${shareUrl}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return <div className="loading">Loading match preview...</div>;
  }

  if (error) {
    return (
      <div className="public-match-preview">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="public-match-preview">
        <div className="empty-state">Match preview not found.</div>
      </div>
    );
  }

  return (
    <div className="public-match-preview">
      <header className="public-match-preview__header">
        {page.clubLogoUrl && (
          <img
            src={mediaUrl(page.clubLogoUrl)}
            alt={`${page.clubName} logo`}
            className="public-match-preview__club-logo"
          />
        )}
        <p className="public-match-preview__club-name">{page.clubName}</p>
        <h1 className="public-match-preview__title">{page.title}</h1>
        {page.opponentName && (
          <p className="public-match-preview__opponent">vs {page.opponentName}</p>
        )}
      </header>

      <div className="public-match-preview__info">
        <p><strong>Date:</strong> {new Date(page.matchDate).toLocaleString()}</p>
        {page.location && <p><strong>Location:</strong> {page.location}</p>}
        {page.description && <p><strong>About:</strong> {page.description}</p>}
      </div>

      {page.previewNotes && (
        <div className="public-match-preview__notes">{page.previewNotes}</div>
      )}

      {page.startingEleven?.length > 0 && (
        <section className="public-match-preview__squad">
          <h2>Starting XI</h2>
          <div className="public-match-preview__players">
            {page.startingEleven.map(renderPlayer)}
          </div>
        </section>
      )}

      {page.substitutes?.length > 0 && (
        <section className="public-match-preview__squad">
          <h2>Substitutes</h2>
          <div className="public-match-preview__players">
            {page.substitutes.map(renderPlayer)}
          </div>
        </section>
      )}

      <div className="public-match-preview__share">
        <button type="button" className="btn btn-secondary" onClick={handleCopyLink}>
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          style={{ backgroundColor: '#25D366' }}
          onClick={handleShareWhatsApp}
        >
          Share on WhatsApp
        </button>
      </div>
    </div>
  );
};

export default PublicMatchPreview;
